import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Fingerprint, Globe2, Loader2, MessageCircle, ShieldCheck } from 'lucide-react';
import { CountrySelect } from '../CountrySelect';
import { fetchFiscalLabels, docInfo, docValid } from '../../lib/fiscalDocs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PURPOSE = 'alterar_dados';

/** Cliente troca CPF ↔ documento internacional confirmando com o código do chat KAKO. */
export const CustomerFiscalChange = ({ account, onUpdated, cardClass = '', inputClass = '' }) => {
  const [step, setStep] = useState(0);      // 0 fechado · 1 dados · 2 código
  const [foreigner, setForeigner] = useState(!!account.is_foreigner);
  const [country, setCountry] = useState(account.country || '');
  const [doc, setDoc] = useState(account.fiscal_document || '');
  const [cpf, setCpf] = useState(account.cpf || '');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [labels, setLabels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchFiscalLabels().then(setLabels); }, []);

  const token = () => localStorage.getItem('customer_token');
  const info = docInfo(labels, country);

  const requestCode = async () => {
    if (foreigner) {
      if (!country) { toast.error('Escolha o seu país.'); return; }
      if (!docValid(labels, country, doc)) {
        toast.error(`${info.label} inválido. Formato aceito: ${info.hint}.`); return;
      }
    } else if (cpf.replace(/\D/g, '').length !== 11) {
      toast.error('Informe o CPF completo (11 dígitos).'); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/kako-auth/request-code`,
        { kako_id: account.kako_id, purpose: PURPOSE });
      setChallengeId(r.data.challenge_id);
      setStep(2);
      toast.success('Código enviado no chat do app KAKO.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível enviar o código agora.');
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const v = await axios.post(`${BACKEND_URL}/api/kako-auth/verify-code`,
        { kako_id: account.kako_id, purpose: PURPOSE, challenge_id: challengeId, code: code.trim() });
      const res = await axios.patch(`${BACKEND_URL}/api/customer-auth/me`, {
        verification_token: v.data.verification_token,
        is_foreigner: foreigner,
        ...(foreigner ? { country, fiscal_document: doc } : { cpf }),
      }, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Dados fiscais atualizados!');
      onUpdated?.(res.data);
      setStep(0); setCode(''); setChallengeId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível concluir a alteração.');
    }
    setBusy(false);
  };

  return (
    <Card className={cardClass} data-testid="fiscal-change-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-display flex items-center gap-2 text-lg tracking-tight text-slate-900">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 text-sky-600">
            <Globe2 className="h-4 w-4" />
          </span>
          Documento e país
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {step === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              {account.is_foreigner
                ? `Conta internacional · ${account.country || '—'} · ${docInfo(labels, account.country).label} ${account.fiscal_document || '—'}`
                : 'Conta brasileira · pagamento no PIX com CPF'}
            </p>
            <Button size="sm" variant="outline" className="rounded-full"
              data-testid="fiscal-change-open" onClick={() => setStep(1)}>
              <Fingerprint className="mr-1.5 h-3.5 w-3.5" /> Alterar documento / país
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="fiscal-change-form">
            <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white/80 p-2.5">
              <Checkbox data-testid="fiscal-foreigner-checkbox" className="mt-0.5"
                checked={foreigner} onCheckedChange={(v) => setForeigner(!!v)} />
              <div className="cursor-pointer" onClick={() => setForeigner((v) => !v)}>
                <p className="text-sm font-semibold text-slate-700">Moro fora do Brasil</p>
                <p className="text-xs text-slate-500">Uso o documento fiscal do meu país, sem CPF</p>
              </div>
            </div>

            {foreigner ? (
              <>
                <CountrySelect testIdPrefix="fiscal-country" exclude={['Brasil']} value={country} onChange={setCountry} />
                <div>
                  <p className="mb-1 text-xs font-semibold text-slate-600">
                    {info.label} <span className="text-slate-400">· {info.hint}</span>
                  </p>
                  <Input data-testid="fiscal-doc-input" value={doc} className={inputClass}
                    disabled={!country}
                    onChange={(e) => setDoc(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    placeholder={country ? info.label : 'Escolha o país primeiro'} />
                  {country && doc && !docValid(labels, country, doc) && (
                    <p className="mt-1 text-xs font-medium text-rose-600" data-testid="fiscal-doc-error">
                      {info.label} de {country} inválido · {info.hint}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <Input data-testid="fiscal-cpf-input" value={cpf} inputMode="numeric" className={inputClass}
                onChange={(e) => setCpf(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="CPF (11 dígitos)" />
            )}

            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <MessageCircle className="h-3.5 w-3.5" /> Vamos enviar um código no chat do app KAKO para confirmar.
            </p>
            <div className="flex gap-2">
              <Button size="sm" data-testid="fiscal-request-code" onClick={requestCode}
                disabled={busy || (foreigner
                  ? (!country || !docValid(labels, country, doc))
                  : cpf.replace(/\D/g, '').length !== 11)}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enviar código'}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full"
                data-testid="fiscal-cancel" onClick={() => setStep(0)}>Cancelar</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="fiscal-code-form">
            <Input data-testid="fiscal-code-input" value={code} inputMode="numeric" className={inputClass}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Código de 6 dígitos" />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy || code.length !== 6}
                data-testid="fiscal-confirm" onClick={confirm}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><ShieldCheck className="mr-1.5 h-4 w-4" /> Confirmar alteração</>
                )}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full"
                data-testid="fiscal-back" onClick={() => setStep(1)}>Voltar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerFiscalChange;
